import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { FileUp, Info } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { DisclaimerNote, SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { createJob, ensureProfile } from "@/lib/jobs.functions";

export const Route = createFileRoute("/_authenticated/upload")({
  head: () => ({
    meta: [
      { title: "Upload your DNA file — Understand Your Genome" },
      {
        name: "description",
        content:
          "Upload your raw DNA data from 23andMe, AncestryDNA, MyHeritage, FamilyTreeDNA, LivingDNA, or a whole-genome VCF. We store it privately and annotate it against public genetic databases.",
      },
      { property: "og:title", content: "Upload your DNA file" },
      {
        property: "og:description",
        content: "Add your VCF or raw genotype file and we'll explain what's inside it.",
      },
    ],
  }),
  component: UploadPage,
});

const MAX_BYTES = 1.5 * 1024 * 1024 * 1024;

// Different providers use different extensions for the same two shapes we
// can actually handle (plain VCF, or a raw genotype export from 23andMe/
// AncestryDNA/MyHeritage/FamilyTreeDNA/LivingDNA) -- .txt is 23andMe's own
// default, FamilyTreeDNA uses .csv, some have none at all. This is just a
// fast client-side sanity check;
// the pipeline itself sniffs actual file CONTENT (see
// pipeline/modules/detect_input_format.nf) and fails clearly if what's
// inside doesn't match either shape, regardless of what this check allowed.
const ACCEPTED_EXTENSIONS = [".vcf", ".vcf.gz", ".txt", ".txt.gz", ".tsv", ".tsv.gz", ".csv", ".csv.gz"];

// No progress event for 30s straight on one chunk means something's
// actually wrong (a dropped connection, a server-side hang) -- not just "a
// slow line still sending bytes," which keeps firing progress events and
// never trips this. Much shorter than the old whole-file version's 90s: a
// chunk is at most UPLOAD_CHUNK_SIZE_BYTES, so a real stall on it shows up
// fast regardless of total file size.
const CHUNK_STALL_TIMEOUT_MS = 30_000;

// A dropped connection mid-chunk retries just that chunk rather than
// failing the whole upload -- the entire point of chunking beyond dodging
// size limits (see storage.server.ts's UPLOAD_CHUNK_SIZE_BYTES comment).
const CHUNK_RETRY_ATTEMPTS = 4;
const CHUNK_RETRY_BASE_DELAY_MS = 2_000;

interface ChunkResponse {
  storage_path?: string;
  uploadId?: string;
  chunkSize?: number;
  etag?: string;
  filename?: string;
  error?: string;
}

/**
 * Posts one request (a JSON control call or a raw chunk of bytes) via
 * XMLHttpRequest, not fetch -- the browser's Fetch API has no
 * upload-progress event at all, only XHR's `xhr.upload.onprogress` does.
 * Shared by every step of the chunked upload flow (start/part/complete/
 * abort); onProgress is only meaningful for the actual byte-carrying /part
 * calls, so it's optional.
 */
function postWithProgress(
  url: string,
  token: string,
  headers: Record<string, string>,
  body: XMLHttpRequestBodyInit,
  onProgress?: (fraction: number) => void,
): Promise<ChunkResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stallTimer: ReturnType<typeof setTimeout>;

    const resetStallTimer = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        xhr.abort();
        reject(new Error("Upload seems stuck — check your connection and try again."));
      }, CHUNK_STALL_TIMEOUT_MS);
    };

    xhr.open("POST", url);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value);

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        resetStallTimer();
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      };
    }
    xhr.onerror = () => {
      clearTimeout(stallTimer);
      reject(new Error("Upload failed — check your connection and try again."));
    };
    xhr.onload = () => {
      clearTimeout(stallTimer);
      let parsed: ChunkResponse = {};
      try {
        parsed = JSON.parse(xhr.responseText);
      } catch {
        // fall through to the status-based handling below
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed);
      } else {
        reject(new Error(parsed.error || `Upload failed (${xhr.status}).`));
      }
    };

    resetStallTimer();
    xhr.send(body);
  });
}

async function withRetry<T>(fn: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < CHUNK_RETRY_ATTEMPTS; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < CHUNK_RETRY_ATTEMPTS - 1) {
        await new Promise((r) => setTimeout(r, CHUNK_RETRY_BASE_DELAY_MS * (attempt + 1)));
      }
    }
  }
  throw lastError;
}

/**
 * Uploads a file as a series of fixed-size chunks via S3-compatible
 * multipart upload (see storage.server.ts for why: this is the only fix
 * that works regardless of Cloudflare plan or any future size limit, and
 * a dropped connection only costs a retry of the current chunk instead of
 * the whole file). Each chunk is retried independently on failure; only
 * once retries are exhausted does the whole upload give up (and best-effort
 * tell the server to abandon the multipart upload).
 */
async function uploadFileInChunks(
  file: File,
  token: string,
  onProgress: (fraction: number) => void,
): Promise<{ storage_path: string }> {
  const start = await postWithProgress(
    "/api/upload/start",
    token,
    { "Content-Type": "application/json" },
    JSON.stringify({ filename: file.name, fileSize: file.size }),
  );
  if (!start.storage_path || !start.uploadId || !start.chunkSize) {
    throw new Error(start.error || "Could not start the upload.");
  }
  const { storage_path, uploadId, chunkSize } = start;

  const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
  const parts: { partNumber: number; etag: string }[] = [];

  try {
    for (let i = 0; i < totalChunks; i++) {
      const chunkStart = i * chunkSize;
      const chunkEnd = Math.min(file.size, chunkStart + chunkSize);
      const chunk = file.slice(chunkStart, chunkEnd);
      const partNumber = i + 1;

      const result = await withRetry(() =>
        postWithProgress(
          "/api/upload/part",
          token,
          {
            "Content-Type": "application/octet-stream",
            "X-Storage-Path": storage_path,
            "X-Upload-Id": uploadId,
            "X-Part-Number": String(partNumber),
          },
          chunk,
          (fraction) => onProgress((i + fraction) / totalChunks),
        ),
      );
      if (!result.etag) throw new Error("Upload failed, please retry.");
      parts.push({ partNumber, etag: result.etag });
      onProgress((i + 1) / totalChunks);
    }
  } catch (error) {
    postWithProgress(
      "/api/upload/abort",
      token,
      { "Content-Type": "application/json" },
      JSON.stringify({ storage_path, uploadId }),
    ).catch(() => {
      // Best-effort cleanup only -- the real error below is what matters.
    });
    throw error;
  }

  const complete = await postWithProgress(
    "/api/upload/complete",
    token,
    { "Content-Type": "application/json" },
    JSON.stringify({ storage_path, uploadId, filename: file.name, parts }),
  );
  if (!complete.storage_path) {
    throw new Error(complete.error || "Could not finish the upload.");
  }
  return { storage_path: complete.storage_path };
}

function isAcceptedFile(name: string) {
  const lower = name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function UploadPage() {
  const [file, setFile] = useState<File | null>(null);
  const [consented, setConsented] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const create = useServerFn(createJob);
  const makeProfile = useServerFn(ensureProfile);

  function pickFile(selected: File | null) {
    if (!selected) return;
    if (!isAcceptedFile(selected.name)) {
      toast.error(
        "That file type isn't supported yet — please choose a VCF or a raw genotype export from your DNA testing provider.",
      );
      return;
    }
    if (selected.size > MAX_BYTES) {
      toast.error("That file is larger than 1.5 GB. Try the compressed .vcf.gz version.");
      return;
    }
    setFile(selected);
  }

  async function handleSubmit() {
    if (!file || !consented) return;
    setUploading(true);
    setProgress(1);

    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error("Your session expired — please sign in again.");

      // Real upload progress scaled to 0-90%; the last 10% covers the
      // quick profile/job-creation calls after the bytes have arrived.
      const { storage_path } = await uploadFileInChunks(file, token, (fraction) => {
        setProgress(Math.round(fraction * 90));
      });

      setProgress(92);
      await makeProfile({});
      await create({ data: { filename: file.name, storage_path } });
      setProgress(100);

      toast.success("Uploaded. We've queued your file for annotation.");
      navigate({ to: "/dashboard" });
    } catch (error) {
      setUploading(false);
      setProgress(0);
      toast.error(error instanceof Error ? error.message : "Something went wrong. Please retry.");
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-5 py-14">
        <h1 className="text-3xl">Upload your file</h1>
        <p className="mt-3 max-w-xl leading-relaxed text-muted-foreground">
          Add the file your testing provider gave you. A VCF file (Variant Call Format) is simply a
          list of the places where your DNA differs from the reference human genome — we'll take it
          from there.
        </p>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            pickFile(e.dataTransfer.files?.[0] ?? null);
          }}
          className={`mt-8 rounded-3xl border-2 border-dashed p-10 text-center transition-colors ${
            dragging ? "border-primary bg-primary/5" : "border-border bg-card"
          }`}
        >
          <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <FileUp className="size-7" />
          </div>
          <p className="mt-4 font-medium">
            {file ? file.name : "Drag your file here, or choose it from your computer"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {file
              ? `${(file.size / (1024 * 1024)).toFixed(1)} MB ready to upload`
              : "VCF, or a raw genotype export from 23andMe/AncestryDNA/MyHeritage/FamilyTreeDNA/LivingDNA — up to 1.5 GB"}
          </p>
          <input
            ref={inputRef}
            type="file"
            accept=".vcf,.txt,.tsv,.csv,.gz,application/gzip,text/plain"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
          <Button
            type="button"
            variant="outline"
            className="mt-5"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {file ? "Choose a different file" : "Choose a file"}
          </Button>
        </div>

        <div className="mt-6 flex gap-3 rounded-2xl bg-secondary/60 p-4 text-sm leading-relaxed text-muted-foreground">
          <Info className="mt-0.5 size-5 shrink-0 text-primary" />
          <p>
            Not sure which file to use? Look for a download named "raw data", "raw DNA data", or
            "variants." 23andMe, AncestryDNA, MyHeritage, FamilyTreeDNA, and LivingDNA raw-data
            exports (often a <code className="font-mono text-xs">.txt</code> or{" "}
            <code className="font-mono text-xs">.csv</code> file) all work as-is — no conversion
            needed on your end. From a whole-genome sequencing provider? Look for anything ending
            in <code className="font-mono text-xs">.vcf</code>. If your file ends in{" "}
            <code className="font-mono text-xs">.gz</code>, that just means it's zipped up to be
            smaller — that's fine, upload it as-is.
          </p>
        </div>

        <label className="mt-8 flex cursor-pointer items-start gap-3 rounded-2xl border border-border bg-card p-4">
          <Checkbox
            checked={consented}
            onCheckedChange={(value) => setConsented(value === true)}
            className="mt-0.5"
          />
          <span className="text-sm leading-relaxed">
            I understand this file will be analyzed for genetic variants and stored securely under
            my account until I delete it. See{" "}
            <a
              href="/privacy"
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
              className="text-primary underline underline-offset-2"
            >
              how your data is handled
            </a>
            .
          </span>
        </label>

        {uploading && (
          <div className="mt-6">
            <Progress value={progress} />
            <p className="mt-2 text-sm text-muted-foreground">Uploading… {progress}%</p>
          </div>
        )}

        <Button
          className="mt-6 w-full bg-accent text-accent-foreground hover:bg-accent/90 sm:w-auto"
          size="lg"
          disabled={!file || !consented || uploading}
          onClick={handleSubmit}
        >
          {uploading ? "Uploading…" : "Upload and start annotation"}
        </Button>

        <DisclaimerNote className="mt-10" />
      </main>
      <SiteFooter />
    </div>
  );
}
